/**
 * Store del feature "Escuchar & Hablar" (speak Fase 19.D) — el sync point.
 *
 * Monta UN ÚNICO `<audio>` (oculto) por ENCIMA de las rutas (en `AppShell`), de
 * modo que el elemento y su estado SOBREVIVEN a navegar `/listen` ↔
 * `/conversation`: el mini-player y el retorno al player conservan posición sin
 * doble reproducción. Toda la UI (biblioteca, player, mini-player) consume
 * `useListen()` — nadie crea otro `<audio>`.
 *
 * El timing del karaoke es REAL: `pos = audio.currentTime`, `duration =
 * audio.duration`, velocidad = `audio.playbackRate`. El `layout` viene del
 * `alignment.json` (timeline original) → el resaltado no se desfasa al cambiar
 * la velocidad. Ver `alignment.ts`.
 *
 * Progreso → `/api/listen/progress` (throttled). Estados de capítulo derivados
 * de `listenedAt`/`conversedAt` (+ "escuchando" = el capítulo activo). El server
 * es never-downgrade.
 *
 * Este archivo es el NÚCLEO (audio element + rendition + transport); el
 * contrato vive en `ListenContext.ts`, la persistencia local en
 * `persistence.ts`, y progreso / navegación / sleep / wake-lock en `hooks/`.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { bindMediaAudioFocus } from '@orborix/voice-client';
import { fetchGlossaryBatch } from '../../lib/lexiconApi.js';
import type { WordLookupEntry } from '../../components/word-lookup/index.js';
import { alignmentToLayout, collectGlossaryWords } from './alignment.js';
import {
  fetchAlignment,
  getCatalog,
  getChapter,
  patchProgress,
  postUnlock,
  ListenApiError,
} from './listenApi.js';
import { bookKeypoints, effectiveLevel } from './bookLib.js';
import { resumePositionFor } from './resume.js';
import {
  ListenContext,
  type AlignmentStatus,
  type ListenContextValue,
} from './ListenContext.js';
import {
  clearSession,
  loadPrefs,
  loadSession,
  savePrefs,
  saveSession,
  SPEED_OPTIONS,
  type PersistedPrefs,
} from './persistence.js';
import { useAudioTransport } from './hooks/useAudioTransport.js';
import { useChapterNav } from './hooks/useChapterNav.js';
import { useListenBootstrap } from './hooks/useListenBootstrap.js';
import { useProgressMap } from './hooks/useProgressMap.js';
import { useAutoAdvance } from './hooks/useAutoAdvance.js';
import { useSleepTimer } from './hooks/useSleepTimer.js';
import { useWakeLock } from './hooks/useWakeLock.js';
import { useMediaSession } from './hooks/useMediaSession.js';
import {
  chapterKey,
  formatPrice,
  type Availability,
  type CatalogBook,
  type CatalogKeypoint,
  type Cefr,
  type ChapterProgress,
  type ChapterRef,
  type Edition,
  type KaraokeLayout,
  type Rendition,
} from './types.js';

// Compat: los consumers históricos importan el contrato desde este módulo.
export { useListen } from './ListenContext.js';
export type {
  AlignmentStatus,
  ListenContextValue,
  NextUp,
  SleepMode,
} from './ListenContext.js';
import { attachShipListenMeter } from '../home/ship/shipListenMeter.js';
import { useOtaApplyHold } from '../../lib/native/useOtaApplyHold.js';
import { isRechargeOffered } from '../../billing/rechargeAvailability.js';
import { track } from '../../lib/analytics.js';

const PROGRESS_THROTTLE_MS = 5000;

export function ListenProvider({ children }: { children: ReactNode }) {
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const prefs0 = useRef<PersistedPrefs>(loadPrefs());
  const [speed, setSpeedState] = useState<number>(prefs0.current.speed);
  const [karaoke, setKaraokeState] = useState<boolean>(prefs0.current.karaoke);
  const [level, setLevelState] = useState<Cefr>(prefs0.current.chosenLevel ?? 'B1');
  // Lo único que se guarda del nivel: lo que el alumno eligió a mano.
  const [chosenLevel, setChosenLevel] = useState<Cefr | null>(prefs0.current.chosenLevel);
  const [voiceId, setVoiceIdState] = useState<string | null>(prefs0.current.voiceId);
  // Idioma preferido (en/es): se prefiere sobre `voiceId` al abrir un capítulo.
  const [prefLang, setPrefLang] = useState<string | null>(prefs0.current.lang);
  const [autoAdvance, setAutoAdvanceState] = useState<boolean>(prefs0.current.autoAdvance);

  // Nivel oficial del user + catálogo (one-shot al montar). Si el user nunca
  // eligió un nivel, el selector arranca en el suyo.
  const { userLevel, catalog, catalogRef } = useListenBootstrap({
    onOfficialLevel: (cefr) => {
      if (prefs0.current.chosenLevel === null) setLevelState(cefr);
    },
  });

  const [book, setBook] = useState<CatalogBook | null>(null);
  const [chapter, setChapter] = useState<CatalogKeypoint | null>(null);
  const [ref, setRef] = useState<ChapterRef | null>(null);
  const [rendition, setRendition] = useState<Rendition | null>(null);
  const [available, setAvailable] = useState<Availability[]>([]);
  const [layout, setLayout] = useState<KaraokeLayout | null>(null);
  const [alignmentStatus, setAlignmentStatus] = useState<AlignmentStatus>('idle');
  const [glossary, setGlossary] = useState<Record<string, WordLookupEntry> | null>(null);
  // Token anti-carrera del karaoke: cada carga de rendition (y cada reintento
  // de alignment) lo incrementa; el layout y el glosario sólo se publican si su
  // token sigue siendo el actual (evita que una carga vieja pise a la nueva al
  // cambiar de capítulo/nivel rápido).
  const karaokeTokenRef = useRef(0);

  const [pos, setPos] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // El user cerró el mini-player global (X). Oculta SÓLO la barra flotante; la
  // sesión sigue (player + home resume). Se re-muestra al volver a reproducir.
  const [dismissed, setDismissed] = useState(false);

  // ─── Unlocks de la visita (Fase 22) ────────────────────────────────────────
  // Complemento en vivo del `unlocked` del catálogo (snapshot). El toast "−X¢"
  // confirma el cobro sin modal; se auto-descarta.
  const [unlockedKeys, setUnlockedKeys] = useState<Record<string, true>>({});
  const [unlockToast, setUnlockToast] = useState<string | null>(null);
  useEffect(() => {
    if (!unlockToast) return;
    const t = window.setTimeout(() => setUnlockToast(null), 2600);
    return () => window.clearTimeout(t);
  }, [unlockToast]);

  // Última sesión de conversación por capítulo (chapterKey → sessionId), para
  // "Seguir la charla" (resumeSessionId). In-memory a propósito: retomar tiene
  // sentido dentro de la misma visita; tras un reload se arranca charla nueva.
  const [coachSessions, setCoachSessions] = useState<Record<string, string>>({});

  // ─── Refs auxiliares (evitan stale closures en listeners del <audio>) ─────
  const refRef = useRef<ChapterRef | null>(null);
  refRef.current = ref;
  /** Rendition activa — la lee `retryAlignment` para re-firmar la MISMA
   *  combinación (cefr/lang/voz) sin depender del estado en su closure. */
  const renditionRef = useRef<Rendition | null>(null);
  renditionRef.current = rendition;
  const levelRef = useRef<Cefr>(level);
  levelRef.current = level;
  /** Combinaciones (cefr, lang, voz) que existen para el capítulo abierto. La
   *  leen `setLevel`/`setVoiceId` para no pedir una que no está grabada. */
  const availableRef = useRef<Availability[]>([]);
  availableRef.current = available;
  const speedRef = useRef<number>(speed);
  speedRef.current = speed;
  /**
   * Seek a aplicar cuando los metadatos del nuevo audio carguen. `abs` =
   * segundos absolutos (resume, mismo timeline); `frac` = fracción 0..1 que se
   * reescala a la nueva duración (cambio de nivel/voz → otro timeline).
   */
  const pendingSeekRef = useRef<{ abs?: number; frac?: number } | null>(null);
  /** Reproducir automáticamente al cargar metadatos. */
  const pendingPlayRef = useRef(false);
  const lastPatchAtRef = useRef(0);
  /** Fin (en segundos del timeline) del tramo en reproducción vía `playSegment`,
   *  o null si no hay tramo activo. El listener `timeupdate` pausa al cruzarlo. */
  const segmentEndRef = useRef<number | null>(null);
  /**
   * Re-firma de URL expirada (Fase 22): en prod el audio es una URL S3 firmada
   * con TTL ~10 min — una sesión pausada larga puede expirar y el `<audio>`
   * emite `error` al reanudar/seekear. Reintentamos UNA vez re-pidiendo el
   * capítulo (re-firma) y reanudando en la misma posición; el flag evita loops
   * si el error es permanente. Se limpia en cada carga exitosa (`onLoaded`).
   */
  const signRetryRef = useRef(false);

  // ─── Progreso / estados (mapa + marcas optimistas) ─────────────────────────
  const {
    setProgressMap,
    statuses,
    statusOf,
    progressOf,
    refreshProgress,
    markConversed,
    markReviewed,
  } = useProgressMap({ refRef, ref, playing, pos });

  // ─── Persistir prefs ───────────────────────────────────────────────────
  useEffect(() => {
    savePrefs({ speed, karaoke, chosenLevel, voiceId, lang: prefLang, autoAdvance });
  }, [speed, karaoke, chosenLevel, voiceId, prefLang, autoAdvance]);

  // ─── Snapshot de sesión (restore tras F5) ─────────────────────────────────
  // Abrir/cambiar de capítulo escribe el snapshot (preservando la posición si
  // es el MISMO capítulo — el caso restore, donde el seek todavía no corrió).
  // La posición fina la mantiene el listener `timeupdate`. OJO: con ref=null NO
  // limpiamos acá — ref es null en CADA arranque (y este provider monta antes
  // que las pantallas, gateadas por RequireAuth), así que limpiar desde este
  // efecto borraba el snapshot ANTES de que el player llegara a restaurarlo.
  // Limpiar es responsabilidad exclusiva de `endSession` (intención explícita).
  useEffect(() => {
    if (!ref) return;
    const prev = loadSession();
    const samePos = prev && chapterKey(prev.ref) === chapterKey(ref) ? prev.pos : 0;
    saveSession({ ref, pos: samePos, cefr: levelRef.current });
  }, [ref]);

  // ─── PATCH de progreso (throttled) ────────────────────────────────────────
  const persistPosition = useCallback((force = false, opts: { keepalive?: boolean } = {}) => {
    const r = refRef.current;
    const audio = audioRef.current;
    if (!r || !audio) return;
    // El <audio> todavía no está POSICIONADO en el capítulo de `refRef`:
    // `load()` resetea readyState a 0 y dispara `pause`, y el seek de resume
    // recién se aplica en `loadedmetadata`. Persistir en esa ventana escribía
    // `currentTime` = 0 sobre la posición guardada del capítulo que se estaba
    // abriendo — perdías el momento justo al ir a retomarlo.
    if (pendingSeekRef.current != null || audio.readyState === 0) return;
    const now = Date.now();
    if (!force && now - lastPatchAtRef.current < PROGRESS_THROTTLE_MS) return;
    lastPatchAtRef.current = now;
    void patchProgress(
      {
        ...r,
        lastPositionSec: Math.floor(audio.currentTime),
        cefr: levelRef.current,
      },
      opts,
    ).catch(() => {
      /* el progreso es best-effort; no rompemos la reproducción por esto */
    });
  }, []);

  // ─── Flush al cerrar / ocultar la pestaña ─────────────────────────────────
  // El PATCH va throttled a 5s: cerrar la ventana perdía hasta los últimos 5
  // segundos escuchados. `pagehide` + `visibilitychange:hidden` son el par que
  // SÍ dispara en móvil (donde `beforeunload` no es confiable), y `keepalive`
  // hace que el request sobreviva al unload.
  useEffect(() => {
    const flush = (): void => persistPosition(true, { keepalive: true });
    const onVisibility = (): void => {
      if (document.visibilityState === 'hidden') flush();
    };
    window.addEventListener('pagehide', flush);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('pagehide', flush);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [persistPosition]);

  // ─── «Armá tu nave»: la Antena cuenta lo escuchado de verdad (ver shipListenMeter) ──
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    return attachShipListenMeter(audio);
  }, []);

  // ─── Listeners del <audio> ────────────────────────────────────────────────
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onTime = (): void => {
      setPos(audio.currentTime);
      // Tramo activo (playSegment): pausa al cruzar el fin de la oración.
      const segEnd = segmentEndRef.current;
      if (segEnd != null && audio.currentTime >= segEnd) {
        segmentEndRef.current = null;
        audio.pause();
      }
      if (!audio.paused) persistPosition(false);
      // Snapshot por pestaña (restore tras F5). El PATCH al server va
      // throttled (5s), pero acá escribimos siempre: tras un reload queremos
      // retomar en el segundo exacto, no hasta 5s atrás.
      const r = refRef.current;
      if (r) saveSession({ ref: r, pos: audio.currentTime, cefr: levelRef.current });
    };
    const onLoaded = (): void => {
      signRetryRef.current = false; // carga OK → habilita un futuro reintento de firma
      setDuration(Number.isFinite(audio.duration) ? audio.duration : 0);
      audio.playbackRate = speedRef.current;
      if (pendingSeekRef.current != null) {
        const { abs, frac } = pendingSeekRef.current;
        const d = Number.isFinite(audio.duration) ? audio.duration : 0;
        const target = frac != null ? frac * d : (abs ?? 0);
        audio.currentTime = Math.min(Math.max(0, target), d || target);
        pendingSeekRef.current = null;
      }
      setPos(audio.currentTime);
      if (pendingPlayRef.current) {
        pendingPlayRef.current = false;
        void audio.play().catch(() => undefined);
      }
    };
    const onPlay = (): void => {
      setPlaying(true);
      setDismissed(false); // volver a reproducir re-muestra la barra flotante
    };
    const onPause = (): void => {
      setPlaying(false);
      persistPosition(true);
    };
    const onError = (): void => {
      // URL firmada expirada (prod) u otro fallo de fuente: un (1) reintento
      // re-firmando vía getChapter, reanudando en la posición actual.
      const r = refRef.current;
      if (!r || signRetryRef.current) return;
      signRetryRef.current = true;
      const resumeAt = audio.currentTime;
      const wasPlaying = pendingPlayRef.current || !audio.paused;
      void loadRenditionRef.current?.(r, {
        cefr: levelRef.current,
        seekAbs: resumeAt,
        ...(wasPlaying ? { play: true } : {}),
      });
    };
    const onEnded = (): void => {
      setPlaying(false);
      // Sin esto `pos` queda en el último `timeupdate`, que puede caer corto
      // del umbral de "terminado" (sobre todo en background) y el panel de
      // fin / auto-avance no se disparaban.
      setPos(audio.duration || audio.currentTime);
      const r = refRef.current;
      if (r) {
        const key = chapterKey(r);
        setProgressMap((m) => ({
          ...m,
          [key]: {
            // Terminado ⇒ no hay posición que retomar (misma invariante que el
            // PATCH del server, que también lo deja en 0).
            lastPositionSec: 0,
            lastCefr: levelRef.current,
            listenedAt: m[key]?.listenedAt ?? new Date().toISOString(),
            reviewedAt: m[key]?.reviewedAt ?? null,
            conversedAt: m[key]?.conversedAt ?? null,
          },
        }));
        void patchProgress({ ...r, listened: true, cefr: levelRef.current }).catch(() => undefined);
      }
    };

    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('loadedmetadata', onLoaded);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('error', onError);
    // Un solo audio a la vez en toda la app: la barra flotante sobrevive a la
    // navegación, así que el libro seguiría sonando debajo de un replay de
    // burbuja o del coach en vivo. Al perder el foco se PAUSA (no se descarga):
    // la posición queda donde estaba y el play la retoma.
    const unbindFocus = bindMediaAudioFocus(audio, 'speak:listen');
    return () => {
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('loadedmetadata', onLoaded);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('error', onError);
      unbindFocus();
    };
  }, [persistPosition, setProgressMap]);

  /** Ref estable a `loadRendition` para el listener `error` (se asigna abajo). */
  const loadRenditionRef = useRef<
    ((r: ChapterRef, opts: { cefr: Cefr; seekAbs?: number; play?: boolean }) => Promise<void>) | null
  >(null);

  // ─── Alignment (karaoke) + glosario ───────────────────────────────────────
  /**
   * Baja el `alignment.json` de una rendition, publica el layout del karaoke y
   * dispara el glosario del capítulo. NUNCA tira: sin karaoke el audio suena
   * igual, así que un fallo acá no puede tumbar la reproducción.
   *
   * Lo que sí hace (y antes no) es dejar asentado POR QUÉ no hay transcripción.
   * Acá vivía un `catch {}` mudo que igualaba dos casos muy distintos: que la
   * narración no tenga alignment (`absent` — el archivo no existe o
   * `alignment_url` quedó NULL en `listen_renditions`) y que sí lo tenga pero
   * la bajada falle (`error` — red, 404 del bucket, URL firmada vencida). Con
   * el mismo mensaje para los dos, un problema de ENTREGA se leía como
   * contenido faltante y quedaba invisible. Ahora el error se loguea con su URL
   * y `alignmentStatus` deja que la UI diga cuál de las dos pasó.
   *
   * `token` es el del karaoke (ver `karaokeTokenRef`): sólo la carga más
   * reciente publica, para que una rendition vieja no pise a la nueva.
   */
  const loadAlignment = useCallback(
    async (url: string | null, lang: string | null, token: number): Promise<void> => {
      // `retryAlignment` llama acá DESPUÉS de un await (la re-firma), así que el
      // token puede haber quedado viejo antes de tocar nada.
      if (token !== karaokeTokenRef.current) return;
      setGlossary(null);
      if (!url) {
        setLayout(null);
        setAlignmentStatus('absent');
        return;
      }
      setAlignmentStatus('loading');
      let lay: KaraokeLayout;
      try {
        lay = alignmentToLayout(await fetchAlignment(url));
      } catch (err) {
        if (token !== karaokeTokenRef.current) return;
        setLayout(null);
        setAlignmentStatus('error');
        console.warn('[listen] alignment fetch failed', { url, error: err });
        return;
      }
      if (token !== karaokeTokenRef.current) return;
      setLayout(lay);
      setAlignmentStatus('ok');

      // Glosario del capítulo (tap-to-lookup) — sólo renditions EN. Best-effort
      // y fuera del camino crítico: la reproducción no espera por esto. Las
      // palabras se enumeran del MISMO layout que se renderiza (garantía de
      // cobertura). Ver `chapter-glossary.ts` en el backend.
      if (lang === 'en') {
        const words = collectGlossaryWords(lay);
        void fetchGlossaryBatch(words)
          .then((g) => {
            if (token === karaokeTokenRef.current) setGlossary(g);
          })
          .catch((err: unknown) => {
            if (token === karaokeTokenRef.current) setGlossary(null);
            console.warn('[listen] glossary fetch failed', err);
          });
      }
    },
    [],
  );

  /**
   * Reintenta bajar la transcripción de la rendition activa (CTA del estado
   * `error`). Re-pide el capítulo SÓLO para que el backend re-firme la URL: en
   * prod la firma vence a los ~10 min, así que reintentar la misma URL fallaría
   * igual. No toca el `<audio>` — el reintento no interrumpe la reproducción —
   * y no re-cobra (el capítulo ya está desbloqueado).
   */
  const retryAlignment = useCallback((): void => {
    const r = refRef.current;
    const rend = renditionRef.current;
    if (!r || !rend) return;
    const token = ++karaokeTokenRef.current;
    setAlignmentStatus('loading');
    void getChapter(r, { cefr: rend.cefr, lang: rend.lang, voice: rend.voiceSlug })
      .then((data) => loadAlignment(data.rendition.alignmentUrl, data.rendition.lang, token))
      .catch((err: unknown) => {
        if (token !== karaokeTokenRef.current) return;
        setAlignmentStatus('error');
        console.warn('[listen] alignment retry failed', err);
      });
  }, [loadAlignment]);

  // ─── Cargar una rendition (resuelve audio + alignment) ────────────────────
  const loadRendition = useCallback(
    async (
      r: ChapterRef,
      opts: {
        cefr: Cefr;
        voice?: string | null;
        /** Idioma de narración preferido (en/es). Mutuamente preferido a `voice`
         * al abrir: deja que el backend elija la voz del libro para ese idioma. */
        lang?: string | null;
        /** Seek absoluto en segundos (resume, mismo timeline). */
        seekAbs?: number;
        /** Seek como fracción 0..1 (cambio de nivel/voz → otro timeline). */
        seekFrac?: number;
        play?: boolean;
      },
    ): Promise<void> => {
      setLoading(true);
      setError(null);
      // Nueva carga → invalida cualquier alignment/glosario en vuelo y limpia
      // el glosario actual.
      const karaokeToken = ++karaokeTokenRef.current;
      setGlossary(null);
      try {
        let data;
        try {
          data = await getChapter(r, {
            cefr: opts.cefr,
            ...(opts.voice ? { voice: opts.voice } : {}),
            ...(opts.lang ? { lang: opts.lang } : {}),
          });
        } catch (e) {
          // 404 `rendition_not_available`. `lang` es una PREFERENCIA que viaja
          // entre capítulos (`prefLang`) y el NIVEL manda: si ese nivel no está
          // grabado en ese idioma, se toma la narración del nivel. `voice`, en
          // cambio, es una elección EXPLÍCITA del usuario: si esa voz no existe,
          // no la sustituimos por otra en silencio.
          //
          // Ese reintento mudo era el bug del selector de voz: las voces son por
          // (libro, NIVEL) —el español vive sólo en A1, ver
          // `content/book-summaries/README.md`— así que tocar «Martín» en B1
          // daba 404, el reintento devolvía la MISMA voz de antes y la hoja se
          // cerraba como si hubiera funcionado. Hoy quien pide una voz pide
          // también el nivel donde existe (`setVoiceId`); si aun así no está, el
          // error sube y la UI lo dice.
          if (
            e instanceof ListenApiError &&
            e.code === 'rendition_not_available' &&
            e.available &&
            !opts.voice &&
            e.available.some((a) => a.cefr === opts.cefr)
          ) {
            data = await getChapter(r, { cefr: opts.cefr });
          } else {
            throw e;
          }
        }

        // ── Unlock transparente (Fase 22) ────────────────────────────────────
        // Capítulo bloqueado: cobrarlo y reproducirlo es UN gesto (sin modal).
        // El POST es idempotente (no re-cobra) y devuelve la rendition exacta
        // ya firmada. Saldo insuficiente → 402 → mensaje con CTA (catch abajo).
        if (data.locked) {
          const unlock = await postUnlock(r, {
            cefr: data.rendition.cefr,
            lang: data.rendition.lang,
            voice: data.rendition.voiceSlug,
          });
          setUnlockedKeys((m) => ({ ...m, [chapterKey(r)]: true }));
          if (!unlock.alreadyUnlocked && unlock.charged !== '0') {
            setUnlockToast(`−${formatPrice(unlock.charged)}`);
            // Mismo canal que los turnos de voz: BalancePill/useCredits hacen
            // el descuento optimista sin otro fetch.
            window.dispatchEvent(
              new CustomEvent('orborix:session.cost.updated', {
                detail: { deltaNano: unlock.charged },
              }),
            );
          }
          if (unlock.rendition) {
            data = { ...data, locked: false, rendition: unlock.rendition };
          } else {
            // La combinación exacta no vino (no debería pasar: la pedimos con la
            // rendition ya resuelta) — re-fetch ahora que está desbloqueado.
            data = await getChapter(r, { cefr: data.rendition.cefr });
          }
        }

        setRendition(data.rendition);
        setAvailable(data.available);
        setVoiceIdState(data.rendition.voiceSlug);
        setPrefLang(data.rendition.lang);
        setLevelState(data.rendition.cefr);
        if (data.progress) {
          setProgressMap((m) => ({ ...m, [chapterKey(r)]: data.progress as ChapterProgress }));
        }

        // Alignment (karaoke) + glosario. El fallo NO tumba la reproducción,
        // pero tampoco se traga: queda en `alignmentStatus` y en la consola.
        await loadAlignment(data.rendition.alignmentUrl, data.rendition.lang, karaokeToken);

        // Punto de resume / seek pedido (la regla vive en `resumePositionFor`).
        if (opts.seekFrac != null) {
          pendingSeekRef.current = { frac: opts.seekFrac };
        } else if (opts.seekAbs != null) {
          pendingSeekRef.current = { abs: opts.seekAbs };
        } else {
          pendingSeekRef.current = {
            abs: resumePositionFor(data.progress, data.rendition.cefr),
          };
        }
        pendingPlayRef.current = !!opts.play;

        const audio = audioRef.current;
        if (audio && data.rendition.audioUrl) {
          audio.src = data.rendition.audioUrl;
          audio.load();
        }
      } catch (e) {
        // 402: saldo insuficiente para el unlock (D3) — mensaje accionable.
        // `rendition_not_available` llegaba crudo a la pantalla (el `message` es
        // el código del backend). Es un estado NORMAL del catálogo —esa
        // combinación no está grabada—, así que se dice en criollo.
        const notRecorded =
          e instanceof ListenApiError && e.code === 'rendition_not_available'
            ? opts.voice
              ? `Esa voz no está grabada para este capítulo en ${opts.cefr}.`
              : `Este capítulo todavía no tiene narración en ${opts.cefr}.`
            : null;
        const msg =
          e instanceof ListenApiError && e.code === 'insufficient_credits'
            ? `Te falta saldo para desbloquear este capítulo (${formatPrice(e.priceNano ?? '0')}).${isRechargeOffered() ? ' Cargá orbs desde Inicio.' : ''}`
            : (notRecorded ??
              (e instanceof ListenApiError ? e.message : 'No pudimos cargar el capítulo.'));
        setError(msg);
        console.warn('[listen] loadRendition error', msg);
      } finally {
        setLoading(false);
      }
    },
    [setProgressMap, loadAlignment],
  );

  loadRenditionRef.current = loadRendition;

  // ─── Abrir un capítulo (desde la biblioteca) ──────────────────────────────
  const openChapter = useCallback(
    async (
      b: CatalogBook,
      kp: CatalogKeypoint,
      edition: Edition,
      opts?: { play?: boolean; cefr?: Cefr },
    ): Promise<void> => {
      const newRef: ChapterRef = { book: b.slug, edition, keypoint: kp.slug };
      setBook(b);
      setChapter(kp);
      setRef(newRef);
      setPos(0);
      setDuration(0);
      // Nivel a pedir: explícito > nivel actual si el capítulo lo tiene > nivel
      // oficial del user > primero disponible del capítulo. El criterio vive en
      // `effectiveLevel` porque la ficha del libro tiene que pedir la cobertura
      // con EXACTAMENTE el mismo, o muestra un número distinto del que suena.
      const cefr = effectiveLevel(kp.levels, opts?.cefr ?? level, userLevel);
      const playOpt = opts?.play ?? false;
      // Preferimos el IDIOMA (no la voz exacta): las voces son por-libro, así que
      // forzar `voiceId` de otro libro daba 404. Con `lang` el backend elige la
      // voz del libro para ese idioma. La voz puntual se cambia en el player.
      track('listen_chapter_started', { book: b.slug, chapter: kp.slug, edition, cefr });
      await loadRendition(newRef, { cefr, lang: prefLang, ...(playOpt ? { play: true } : {}) });
    },
    [level, userLevel, prefLang, loadRendition],
  );

  // ─── Restaurar la sesión de la pestaña (F5 en /listen/player) ─────────────
  // Rehidrata el capítulo del snapshot de sessionStorage: resuelve book/chapter
  // contra el catálogo (esperándolo si todavía no llegó) y recarga la rendition
  // PAUSADA en la posición exacta del snapshot (autoplay tras reload está
  // bloqueado por el browser de todos modos). Devuelve si quedó algo abierto.
  // Dedupe de llamadas concurrentes (StrictMode monta efectos dos veces).
  const restoreInFlightRef = useRef<Promise<boolean> | null>(null);
  const restoreSession = useCallback((): Promise<boolean> => {
    if (restoreInFlightRef.current) return restoreInFlightRef.current;
    const run = async (): Promise<boolean> => {
      if (refRef.current) return true; // ya hay capítulo abierto
      const snap = loadSession();
      if (!snap) return false;
      let books = catalogRef.current;
      if (books.length === 0) {
        try {
          books = (await getCatalog()).books;
        } catch {
          return false;
        }
      }
      const b = books.find((x) => x.slug === snap.ref.book);
      const kp = b
        ? bookKeypoints(b, snap.ref.edition).find((k) => k.slug === snap.ref.keypoint)
        : undefined;
      if (!b || !kp) {
        // El snapshot apunta a un capítulo que ya no existe en el catálogo.
        clearSession();
        return false;
      }
      setBook(b);
      setChapter(kp);
      setRef(snap.ref);
      setPos(0);
      setDuration(0);
      // Mismo criterio de nivel que `openChapter`: el del snapshot si el
      // capítulo lo ofrece; si no, el primero disponible.
      const want = snap.cefr ?? levelRef.current;
      const cefr: Cefr = kp.levels.includes(want) ? want : (kp.levels[0] ?? want);
      await loadRendition(snap.ref, { cefr, lang: prefLang, seekAbs: snap.pos });
      return true;
    };
    const p = run().finally(() => {
      restoreInFlightRef.current = null;
    });
    restoreInFlightRef.current = p;
    return p;
  }, [prefLang, loadRendition]);

  // ─── Transport (play/pause/seek/segment/speed) ────────────────────────────
  const { play, playSegment, pause, toggle, close, seek, skip, setSpeed } = useAudioTransport({
    audioRef,
    segmentEndRef,
    setPos,
    setDismissed,
    setSpeedState,
  });

  // "Seguir la charla": memoria in-session de la última charla por capítulo.
  const coachSessionOf = useCallback(
    (r: ChapterRef): string | null => coachSessions[chapterKey(r)] ?? null,
    [coachSessions],
  );
  const rememberCoachSession = useCallback((r: ChapterRef, sessionId: string): void => {
    setCoachSessions((m) => ({ ...m, [chapterKey(r)]: sessionId }));
  }, []);

  const setKaraoke = useCallback((on: boolean): void => setKaraokeState(on), []);
  const setAutoAdvance = useCallback((on: boolean): void => setAutoAdvanceState(on), []);

  // Cambiar nivel: preserva la fracción de avance (timeline distinto por nivel).
  const setLevel = useCallback(
    (cefr: Cefr): void => {
      setChosenLevel(cefr);
      const r = refRef.current;
      if (!r) {
        setLevelState(cefr);
        return;
      }
      const audio = audioRef.current;
      const frac = audio && audio.duration > 0 ? audio.currentTime / audio.duration : 0;
      const wasPlaying = audio ? !audio.paused : false;
      // La voz es por (libro, nivel): al cambiar de nivel sólo la arrastramos si
      // ESE nivel la tiene grabada; si no, el backend elige la del nivel. Antes
      // se pedía siempre y el 404 lo tapaba un reintento invisible.
      const keepVoice =
        voiceId !== null &&
        availableRef.current.some((a) => a.cefr === cefr && a.voiceSlug === voiceId);
      void loadRendition(r, {
        cefr,
        ...(keepVoice ? { voice: voiceId } : {}),
        seekFrac: frac, // se reescala a la nueva duración en onLoaded
        ...(wasPlaying ? { play: true } : {}),
      });
    },
    [voiceId, loadRendition],
  );

  // Cambiar voz: re-resuelve la rendition con esa voz (puede implicar otro lang).
  // `cefr` explícito = la voz elegida vive en OTRO nivel y se lo lleva puesto:
  // el español está grabado sólo en A1 (andamiaje por nivel, ver
  // `content/book-summaries/README.md`), así que elegir a Martín desde B1 es
  // pedir el capítulo en A1. La hoja de voz lo dice antes de que lo toques.
  const setVoiceId = useCallback(
    (voiceSlug: string, cefr?: Cefr): void => {
      if (cefr) setChosenLevel(cefr);
      const r = refRef.current;
      if (!r) {
        // Sin capítulo abierto no hay nada que recargar: queda de preferencia.
        setVoiceIdState(voiceSlug);
        return;
      }
      // Con capítulo abierto NO lo marcamos de prepo: `loadRendition` fija la
      // voz REALMENTE cargada, así una carga fallida no deja la UI marcando
      // una voz que no está sonando.
      const audio = audioRef.current;
      const frac = audio && audio.duration > 0 ? audio.currentTime / audio.duration : 0;
      const wasPlaying = audio ? !audio.paused : false;
      void loadRendition(r, {
        cefr: cefr ?? levelRef.current,
        voice: voiceSlug,
        seekFrac: frac,
        ...(wasPlaying ? { play: true } : {}),
      });
    },
    [loadRendition],
  );

  // ─── Navegación de capítulos + sueño + wake lock ───────────────────────────
  const { hasNext, hasPrev, nextUp, goNextChapter, goPrevChapter } = useChapterNav({
    catalog,
    ref,
    audioRef,
    openChapter,
    seek,
  });
  const { sleepMode, sleepSecs, setSleep, cancelSleep } = useSleepTimer({ playing, audioRef });
  useWakeLock(playing);
  // Native OTA (lib/native/ota.ts): no bundle update while the audio plays — the
  // plugin applies on background, and background playback is a real use of /listen.
  useOtaApplyHold('listen-playback', playing);
  // Controles de media del SO (lockscreen, auriculares, widget) — A.5. Vive acá,
  // junto al wake lock, porque este provider es dueño del único <audio>.
  useMediaSession({
    book,
    chapter,
    playing,
    pos,
    duration: duration || rendition?.durationSec || 0,
    play,
    pause,
    seek,
    skip,
    hasNext,
    hasPrev,
    goNextChapter,
    goPrevChapter,
  });

  // Auto-avance al próximo capítulo (vive acá y no en el player screen: tiene
  // que funcionar minimizado y con la pantalla apagada). Ver useAutoAdvance.
  const { countdown: autoAdvanceCountdown, cancelAutoAdvance } = useAutoAdvance({
    pos,
    duration: duration || rendition?.durationSec || 0,
    autoAdvance,
    sleepMode,
    cancelSleep,
    hasNext,
    goNextChapter,
  });

  // Termina la sesión de escucha completa (X del player). Persistimos la
  // posición ANTES de limpiar (después refRef ya es null) y desmontamos el
  // capítulo en memoria — el <audio> queda sin src, los seeks/play pendientes
  // se cancelan y el glosario en vuelo se invalida. El progreso server-side no
  // se toca: "Seguí escuchando" sigue funcionando desde biblioteca/home.
  const endSession = useCallback((): void => {
    persistPosition(true);
    clearSession(); // la X olvida la sesión de la pestaña (no se restaura tras F5)
    const audio = audioRef.current;
    audio?.pause();
    segmentEndRef.current = null;
    pendingSeekRef.current = null;
    pendingPlayRef.current = false;
    karaokeTokenRef.current++;
    if (audio) {
      audio.removeAttribute('src');
      audio.load();
    }
    setBook(null);
    setChapter(null);
    setRef(null);
    setRendition(null);
    setAvailable([]);
    setLayout(null);
    setAlignmentStatus('idle');
    setGlossary(null);
    setPos(0);
    setDuration(0);
    setDismissed(false);
    cancelSleep();
  }, [persistPosition, cancelSleep]);

  // Persistir posición al desmontar (cierre de pestaña / navegación dura).
  useEffect(() => {
    return () => persistPosition(true);
  }, [persistPosition]);

  /**
   * Posición instantánea del audio, para quien necesite más resolución que el
   * estado `pos` (que sigue al `timeupdate`, ~4 Hz). Estable — no re-renderiza:
   * el consumidor decide con qué frecuencia leerla (el karaoke lo hace por rAF).
   * El `?? 0` no enmascara config mal resuelta: el `<audio>` legítimamente no
   * está montado antes del primer render, y "sin audio" es la posición 0.
   */
  const getPositionNow = useCallback((): number => audioRef.current?.currentTime ?? 0, []);

  const value = useMemo<ListenContextValue>(
    () => ({
      book,
      chapter,
      ref,
      isOpen: ref != null,
      rendition,
      available,
      level,
      effLevel: rendition?.cefr ?? null,
      voiceId,
      lang: rendition?.lang ?? null,
      layout,
      alignmentStatus,
      retryAlignment,
      karaoke,
      glossary,
      pos,
      getPositionNow,
      duration: duration || rendition?.durationSec || 0,
      playing,
      dismissed,
      speed,
      speedOptions: SPEED_OPTIONS,
      loading,
      error,
      statuses,
      statusOf,
      progressOf,
      refreshProgress,
      unlockedKeys,
      openChapter,
      restoreSession,
      play,
      pause,
      toggle,
      close,
      endSession,
      coachSessionOf,
      rememberCoachSession,
      seek,
      skip,
      setLevel,
      setVoiceId,
      setKaraoke,
      setSpeed,
      autoAdvance,
      setAutoAdvance,
      autoAdvanceCountdown,
      cancelAutoAdvance,
      markConversed,
      markReviewed,
      playSegment,
      hasNext,
      hasPrev,
      nextUp,
      goNextChapter,
      goPrevChapter,
      sleepMode,
      sleepSecs,
      setSleep,
      cancelSleep,
      userLevel,
    }),
    [
      book,
      chapter,
      ref,
      rendition,
      available,
      level,
      voiceId,
      layout,
      alignmentStatus,
      retryAlignment,
      karaoke,
      glossary,
      pos,
      getPositionNow,
      duration,
      playing,
      dismissed,
      speed,
      loading,
      error,
      statuses,
      statusOf,
      progressOf,
      refreshProgress,
      unlockedKeys,
      openChapter,
      restoreSession,
      play,
      pause,
      toggle,
      close,
      endSession,
      coachSessionOf,
      rememberCoachSession,
      seek,
      skip,
      setLevel,
      setVoiceId,
      setKaraoke,
      setSpeed,
      autoAdvance,
      setAutoAdvance,
      autoAdvanceCountdown,
      cancelAutoAdvance,
      markConversed,
      markReviewed,
      playSegment,
      hasNext,
      hasPrev,
      nextUp,
      goNextChapter,
      goPrevChapter,
      sleepMode,
      sleepSecs,
      setSleep,
      cancelSleep,
      userLevel,
    ],
  );

  return (
    <ListenContext.Provider value={value}>
      {children}
      {/* Único <audio> del feature — vive sobre las rutas, persiste al navegar. */}
      <audio ref={audioRef} preload="metadata" hidden />
      {/* Toast de cobro de unlock (Fase 22): confirmación sutil, sin modal. */}
      {unlockToast && (
        <div className="ls-unlock-toast" role="status" aria-live="polite">
          {unlockToast}
        </div>
      )}
    </ListenContext.Provider>
  );
}
